import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { useAuthStore } from "./authStore";
import { Address } from "@/lib/types";
import { CurrencyCode } from "@forgepoint/shared";

// Define the organization type
export type Organization = {
  id: string;
  name: string;
  slug?: string;
  address?: Address;
  phone?: string;
  fax?: string;
  website?: string;
  logoAssetId?: string;
  logoUrl?: string;
  baseCurrency?: CurrencyCode;
  primaryAgentWorkflowId?: string;
  standardAgentEnabled?: boolean;
  multiCurrencyEnabled?: boolean;
  bomSequenceNumbersEnabled?: boolean;
};

// Define the state shape
interface OrganizationState {
  organization: Organization | null;
  loading: boolean;
  error: string | null;
}

// Define the actions
interface OrganizationActions {
  fetchOrganization: () => Promise<void>;
  updateOrganization: (data: {
    address?: Address;
    phone?: string;
    fax?: string;
    website?: string;
    logoAssetId?: string;
    baseCurrency?: CurrencyCode;
    primaryAgentWorkflowId?: string | null;
    standardAgentEnabled?: boolean;
    multiCurrencyEnabled?: boolean;
    bomSequenceNumbersEnabled?: boolean;
  }) => Promise<boolean>;
}

// Define the initial state
const initialState: OrganizationState = {
  organization: null,
  loading: false,
  error: null,
};

// Create the store
export const useOrganizationStore = create<
  OrganizationState & OrganizationActions
>()((set) => ({
  ...initialState,

  // Fetch the current organization
  fetchOrganization: async () => {
    const user = useAuthStore.getState().user;

    if (!user) {
      set({ loading: false, organization: null });
      return;
    }

    set({ loading: true, error: null });

    try {
      const response = await fetchApi("organizations/current", "GET");

      if (!response.ok) {
        throw new Error("Failed to fetch organization");
      }

      const data = response.data as { organization: Organization };
      set({
        organization: data.organization,
        loading: false,
      });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Unknown error occurred",
        loading: false,
      });
    }
  },

  // Update organization data
  updateOrganization: async (data: {
    address?: Address;
    phone?: string;
    fax?: string;
    website?: string;
    logoAssetId?: string;
    baseCurrency?: CurrencyCode;
    primaryAgentWorkflowId?: string | null;
    standardAgentEnabled?: boolean;
    multiCurrencyEnabled?: boolean;
    bomSequenceNumbersEnabled?: boolean;
  }): Promise<boolean> => {
    const user = useAuthStore.getState().user;

    if (!user) {
      return false;
    }

    set({ error: null });

    try {
      const response = await fetchApi("organizations/current", "PUT", {
        body: data,
      });

      if (!response.ok) {
        throw new Error("Failed to update organization");
      }

      const updatedData = response.data as { organization: Organization };
      set({ organization: updatedData.organization });
      return true;
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Unknown error occurred",
      });
      return false;
    }
  },
}));

// Initialize the organization data when this module is imported
// (This is optional, but replicates the useEffect behavior in the original context)
// useOrganizationStore.getState().fetchOrganization();
// It's better to call this explicitly in the App component to avoid race conditions
